import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/buttons/Button';
import { BottomSheet } from '~/components/ui/nucleus/Sheets';

export type MobileBulkAction =
  | 'ALLOCATE'
  | 'RESCHEDULE'
  | 'UPDATE_TEMPLATE'
  | 'TAGS'
  | 'COMPLETE'
  | 'CANCEL';

type Props = {
  selectedCount: number;
  onAction: (action: MobileBulkAction) => void;
};

const MORE_ACTIONS: { key: MobileBulkAction; label: string }[] = [
  { key: 'UPDATE_TEMPLATE', label: 'Update Template' },
  { key: 'TAGS', label: 'Add / Remove Tags' },
  { key: 'COMPLETE', label: 'Complete' },
  { key: 'CANCEL', label: 'Cancel' },
];

/**
 * Sticky thumb-zone actions while mobile Select mode has a non-empty selection.
 * Primary actions stay visible; the rest open in a More sheet.
 */
export function JobListMobileBulkBar({ selectedCount, onAction }: Props) {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      <div
        role='toolbar'
        aria-label={t('selected', { count: selectedCount })}
        className='fixed bottom-0 left-0 right-0 z-40 border-t border-outline-variant bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] lg:hidden'
      >
        <p className='mb-2 text-center text-xs font-medium text-grey-50'>
          {t('selected', { count: selectedCount })}
        </p>
        <div className='flex items-center gap-2'>
          <Button
            as='button'
            type='button'
            color='primary'
            rounded
            className='flex-1'
            onClick={() => onAction('RESCHEDULE')}
          >
            Reschedule
          </Button>
          <Button
            as='button'
            type='button'
            color='primary'
            rounded
            className='flex-1'
            onClick={() => onAction('ALLOCATE')}
          >
            Allocate
          </Button>
          <Button
            as='button'
            type='button'
            color='default'
            rounded
            className='flex-1'
            onClick={() => setMoreOpen(true)}
          >
            More
          </Button>
        </div>
      </div>

      <BottomSheet
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        title='More'
      >
        <div className='flex flex-col gap-2 p-4'>
          {MORE_ACTIONS.map(({ key, label }) => (
            <Button
              key={key}
              as='button'
              type='button'
              color='primary'
              rounded
              className='w-full'
              onClick={() => {
                setMoreOpen(false);
                onAction(key);
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      </BottomSheet>
    </>
  );
}
